import type {LiveEditsState, LocalOpEnvelope, PublishedOpEnvelope} from '@PosterWhiteboard/collaboration/poster-live-edits.types';
import type {PosterOp, SnapshotRecord} from '@PosterWhiteboard/collaboration/poster-op.types';
import {applyPosterOps} from '@PosterWhiteboard/collaboration/apply-poster-ops.library';
import {normalizePosterSnapshot} from '@PosterWhiteboard/collaboration/normalize-poster-snapshot.library';

/**
 * Folds one ordered envelope into the state. The confirmed poster advances whoever sent the
 * envelope; when it was this tab's own, that commit also leaves the pending list, since the
 * server now holds it. Returns the same state when the envelope is not the next in sequence.
 */
export const confirmEnvelope = (state: LiveEditsState, envelope: PublishedOpEnvelope): LiveEditsState => {
  if (envelope.seq !== state.lastAppliedSeq + 1) {
    return state;
  }
  return {
    confirmed: normalizePosterSnapshot(applyPosterOps(state.confirmed, envelope.ops)),
    lastAppliedSeq: envelope.seq,
    pending: state.pending.filter((pendingEnvelope) => pendingEnvelope.opId !== envelope.opId),
  };
};

/**
 * The poster as this tab shows it: the confirmed state with its own unordered commits on top, both
 * the envelopes already sent and the ops still waiting for their envelope.
 */
export const replayLocalEdits = (state: LiveEditsState, bufferedOps: PosterOp[]): SnapshotRecord => {
  const localOps = [...state.pending.flatMap((envelope) => envelope.ops), ...bufferedOps];
  if (localOps.length === 0) {
    return state.confirmed;
  }
  return normalizePosterSnapshot(applyPosterOps(state.confirmed, localOps));
};

export const isEnvelopeFromSession = (envelope: PublishedOpEnvelope, sessionId: string): boolean => envelope.sessionId === sessionId;

export const isPublishedOpEnvelope = (value: unknown): value is PublishedOpEnvelope => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.seq === 'number' &&
    typeof candidate.opId === 'string' &&
    typeof candidate.sessionId === 'string' &&
    typeof candidate.userId === 'number' &&
    typeof candidate.posterVersion === 'number' &&
    Array.isArray(candidate.ops)
  );
};

export const withPendingEnvelope = (state: LiveEditsState, envelope: LocalOpEnvelope): LiveEditsState => ({...state, pending: [...state.pending, envelope]});

export const withoutPendingEnvelopes = (state: LiveEditsState): LiveEditsState => ({...state, pending: []});
